import React from 'react';
import { Link } from 'react-router-dom';
import './TermsOfServicePage.css';

const TermsOfServicePage = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1>Terms of Service</h1>
        <p className="legal-meta">Effective Date: February 26, 2026 &middot; Version 1.0</p>

        <section>
          <p>
            Welcome to <strong>AmpNebula</strong>, a guitar amplifier database and collection management platform
            operated by <strong>QorVertex LLC</strong> ("Company," "we," "us," or "our"). These Terms of Service
            ("Terms") govern your access to and use of our website, applications, and services (collectively, the
            "Service").
          </p>
          <p>
            By creating an account or using the Service you agree to be bound by these Terms. If you do not agree,
            do not use the Service.
          </p>
        </section>

        <section>
          <h2>1. Eligibility</h2>
          <p>You must be at least 13 years old to use the Service. By using the Service you represent that you meet this requirement.</p>
        </section>

        <section>
          <h2>2. Accounts</h2>
          <p>
            You are responsible for maintaining the confidentiality of your login credentials and for all activity
            under your account. You agree to provide accurate, current, and complete information during registration
            and to update it as necessary. Notify us immediately at{' '}
            <a href="mailto:support@ampnebula.com">support@ampnebula.com</a> if you suspect unauthorized use of
            your account.
          </p>
        </section>

        <section>
          <h2>3. User-Generated Content</h2>
          <p>
            You retain ownership of content you submit (photos, descriptions, collection data, etc.). By submitting
            content you grant QorVertex LLC a worldwide, royalty-free, non-exclusive license to use, display, and
            distribute it in connection with the Service. You represent that you have the right to submit any content
            you provide and that it does not infringe any third-party rights.
          </p>
        </section>

        <section>
          <h2>4. AmpNebula Valuation&trade;</h2>
          <p>
            Valuations are algorithmic estimates based on publicly available market data and user-contributed
            information. They are <strong>not</strong> professional appraisals, and QorVertex LLC makes no
            guarantee of accuracy. Valuations should not be relied upon as the sole basis for purchasing, selling,
            or insuring equipment.
          </p>
        </section>

        <section>
          <h2>5. Subscriptions &amp; Billing</h2>
          <p>
            Certain features require a paid subscription processed through Stripe. By subscribing you authorize
            recurring charges at the posted rate. You may cancel at any time; cancellation takes effect at the end
            of the current billing period. Refunds are handled on a case-by-case basis in accordance with our
            refund policy.
          </p>
        </section>

        <section>
          <h2>6. Intellectual Property</h2>
          <p>
            All Service content not created by users—including logos, design, and code—is owned by QorVertex LLC
            and protected by copyright and trademark law. You may not copy, modify, or distribute it without
            written permission.
          </p>
        </section>

        <section>
          <h2>7. Prohibited Conduct</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Violate any applicable law or regulation</li>
            <li>Infringe any intellectual-property or privacy rights</li>
            <li>Upload malicious code or interfere with the Service</li>
            <li>Scrape, crawl, or otherwise harvest data without permission</li>
            <li>Impersonate another person or entity</li>
            <li>Use the Service for spam or unsolicited contact</li>
          </ul>
        </section>

        <section>
          <h2>8. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, QorVertex LLC shall not be liable for any indirect, incidental,
            special, consequential, or punitive damages arising from your use of the Service. Our total liability
            shall not exceed the amount you paid us in the 12 months preceding the claim.
          </p>
        </section>

        <section>
          <h2>9. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless QorVertex LLC, its members, and contractors from any claims,
            damages, or expenses arising from your use of the Service or violation of these Terms.
          </p>
        </section>

        <section>
          <h2>10. Disclaimer of Warranties</h2>
          <p>
            The Service is provided "as is" and "as available" without warranties of any kind, whether express or
            implied, including merchantability, fitness for a particular purpose, and non-infringement.
          </p>
        </section>

        <section>
          <h2>11. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the State of California, without regard to conflict-of-law
            principles. Any disputes shall be resolved in the state or federal courts located in California.
          </p>
        </section>

        <section>
          <h2>12. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. When we do, we will update the version number and
            effective date at the top of this page. Registered users will be notified of material changes and
            asked to re-accept before continuing to use the Service.
          </p>
        </section>

        <section>
          <h2>13. Copyright &amp; DMCA Policy</h2>
          <p>
            QorVertex LLC respects intellectual property rights and complies with the Digital Millennium
            Copyright Act (DMCA), 17 U.S.C. § 512. If you believe that content on AmpNebula infringes
            your copyright, please submit a written notice to our designated copyright agent at{' '}
            <a href="mailto:dmca@ampnebula.com">dmca@ampnebula.com</a>. Full instructions for submitting
            a takedown notice or counter-notice are available on our{' '}
            <Link to="/dmca">DMCA Policy page</Link>.
          </p>
          <p>
            We will terminate the accounts of users determined to be repeat infringers in accordance
            with 17 U.S.C. § 512(i).
          </p>
        </section>

        <section>
          <h2>14. Contact</h2>
          <p>
            Questions about these Terms? Contact us at{' '}
            <a href="mailto:legal@ampnebula.com">legal@ampnebula.com</a>.
          </p>
          <p className="legal-entity">
            <strong>QorVertex LLC</strong>
          </p>
        </section>

        <div className="legal-nav">
          <Link to="/privacy">Privacy Policy</Link>
          {' · '}
          <Link to="/dmca">DMCA Policy</Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
